/**
 * HelpButton — floating "?" control that only appears when the current
 * route has linked help content in the knowledge graph.
 */

import { useEffect, useRef, useState } from 'react'
import { CircleHelp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useRouteHelp } from '@/hooks/useRouteHelp'
import { HelpDrawer } from './HelpDrawer'

interface HelpButtonProps {
  /** Optional override; defaults to the current location pathname. */
  routePath?: string
  /** Extra classes for the floating button. */
  className?: string
}

export function HelpButton({ routePath, className }: HelpButtonProps) {
  const { articles, hasHelp, loading, routePath: resolvedPath } = useRouteHelp(routePath)
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Close the drawer when navigating to a different route
  useEffect(() => {
    setOpen(false)
  }, [resolvedPath])

  // Hide entirely while loading or when no help exists — no empty state UI
  if (loading || !hasHelp) {
    return null
  }

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        size="icon"
        variant="secondary"
        className={className ?? 'fixed bottom-4 right-32 z-40 h-10 w-10 rounded-full shadow-lg'}
        aria-label="Page help"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Help for this page"
        onClick={() => setOpen(true)}
        data-testid="route-help-button"
      >
        <CircleHelp className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">Open help for this page</span>
      </Button>

      <HelpDrawer
        open={open}
        onClose={() => setOpen(false)}
        articles={articles}
        returnFocusRef={buttonRef}
      />
    </>
  )
}
